import { Component, Input, OnInit, OnDestroy, ViewChild } from '@angular/core';
import Icons from 'src/app/core/enums/icons.enum';
import { filter, Subject, takeUntil } from 'rxjs';
import { PortalStateService } from 'src/app/core/services/portal-state.service';
import { Application } from 'src/portaljs/constants/application';
import { MenuItem } from 'src/portaljs/models/menu-item';
import { SelectedMenu } from 'src/app/core/models/selected-menu.model';
import { Message } from 'src/portaljs/models/message';
import { MessageType } from 'src/portaljs/constants/message-type';
import { PortalApp } from 'src/app/core/shared/portal-app';
import { ToolbarService } from 'src/app/core/services/toolbar.service';
import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';

@Component({
    selector: 'fss-portal-menu',
    templateUrl: './menu.component.html',
    styleUrls: ['./menu.component.scss'],
    standalone: false
})
export class MenuComponent implements OnInit, OnDestroy {
  @Input()
  public menu: MenuItem;

  @Input()
  public set inToolbar(value: BooleanInput) {
    this.isInToolbar = coerceBooleanProperty(value);
  }

  @ViewChild('childMenu', {static: true}) public childMenu: any;

  public isInToolbar = false;

  public get hasChildren(): boolean {
    return this.menu.subMenuItems && this.menu.subMenuItems.length > 0;
  }

  public get isHighlighted(): boolean {
    return this.menu.id === this.selectedMenuId;
  }

  public icons = Icons;
  private activeApp: Application;
  private ngUnsubscribe = new Subject();
  public selectedMenuId: string = null;

  constructor(
    private portalStateService: PortalStateService,
    private toolbarService: ToolbarService) {}

  public ngOnInit(): void {
    this.activeAppWatch();
    this.selectedMenuWatch();
  }

  public ngOnDestroy() {
    this.ngUnsubscribe.next(undefined);
    this.ngUnsubscribe.complete();
  }

  public selectedMenuWatch(): void {
    this.portalStateService.getSelectedMenuId$(this.activeApp)
      .pipe(        
        filter((selectedMenuId) => !!selectedMenuId),
        takeUntil(this.ngUnsubscribe)
      )
      .subscribe((selectedMenuId) => {
        const parentId = this.toolbarService.getTopLevelMenuId(selectedMenuId);
        this.selectedMenuId = parentId;
      });
  }

  public activeAppWatch(): void {
    this.portalStateService.getActiveApp$()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((activeApp: PortalApp) => {
        if (activeApp) {
          this.activeApp = activeApp.app;
        }
      });
  }

  public saveSelectedId(itemId: string): void {
    const selectedMenu: SelectedMenu = {
      [this.activeApp]: {
        menuItemId: itemId
      }
    };

    this.portalStateService.setSelectedMenuId(selectedMenu);
  }

  public clickButton(selectedId: string): void {

    let menuApp: Application = this.activeApp;
    const portalApp = this.portalStateService.getSelectableApp(menuApp);
    if (portalApp && portalApp.parent) { // If this is a child app, send the click event to the parent app instead.
      menuApp = portalApp.parent;
    }

    const msg = new Message(selectedId, MessageType.menuItemSelect, Application.PORTAL, menuApp, 'clicked');
    this.portalStateService.postMessage(msg);
    this.toolbarService.isMobileMenuOpen = false;

    const menuItem: MenuItem = this.findMenuItemById(selectedId, this.menu?.subMenuItems);
    if (menuItem && menuItem.externalUrl) {
      window.open(menuItem.externalUrl, '_blank');
    } else if (!menuItem.externalUrl) {
      this.saveSelectedId(selectedId);
    }
  }

  private isRootNodeForMenuItemId(menuItemId: string, items: MenuItem[]): boolean {
    if (!this.isInToolbar) return false;
    if (this.menu.id === menuItemId) return true;

    if (items) {
      for (const subItem of items) {
        if (subItem.id === menuItemId) {
          return true;
        } else if (this.isRootNodeForMenuItemId(menuItemId, subItem.subMenuItems)) {
          return true;
        }
      }
    }
    return false;
  }

  private findMenuItemById(id: string, items: MenuItem[]): MenuItem {
    if (this.menu?.id === id) {
      return this.menu;
    }
    if (items) {
      for (const subItem of items) {
        if (subItem.id === id) {
          return subItem;
        } else {
          const nested = this.findMenuItemById(id, subItem.subMenuItems);
          if (nested) {
            return nested;
          }
        }
      }
    }
    return undefined;
  }
}
